import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  increment,
  Unsubscribe,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { createNotification } from './notificationService';

// Interfaces
export interface FollowUser {
  uid: string;
  displayName: string;
  photoURL: string;
  bio?: string;
  createdAt?: any;
  isFollowing?: boolean;
}

/**
 * Sigue a un usuario
 * @param currentUserId ID del usuario actual
 * @param targetUserId ID del usuario a seguir
 * @param currentUserData Datos del usuario actual
 * @param targetUserData Datos del usuario a seguir
 * @returns Promise<void>
 */
export const followUser = async (
  currentUserId: string,
  targetUserId: string,
  currentUserData: { displayName: string; photoURL: string | null },
  targetUserData: { displayName: string; photoURL: string | null }
): Promise<void> => {
  // Validaciones iniciales
  if (!currentUserId || !targetUserId) {
    console.error('IDs de usuario inválidos');
    throw new Error('Datos de usuario inválidos');
  }

  if (currentUserId === targetUserId) {
    console.error('Un usuario no puede seguirse a sí mismo');
    return; // Simplemente retornamos sin error para evitar problemas en la UI
  }

  try {
    // Referencias a los documentos
    const currentUserRef = doc(db, 'users', currentUserId);
    const targetUserRef = doc(db, 'users', targetUserId);

    // Verificar que ambos usuarios existen
    const [currentUserDoc, targetUserDoc] = await Promise.all([
      getDoc(currentUserRef),
      getDoc(targetUserRef)
    ]);

    if (!currentUserDoc.exists() || !targetUserDoc.exists()) {
      console.error('Uno o ambos usuarios no existen');
      throw new Error('Usuario no encontrado');
    }

    // Referencias a las subcollecciones
    const followingRef = doc(db, `users/${currentUserId}/following`, targetUserId);
    const followerRef = doc(db, `users/${targetUserId}/followers`, currentUserId);

    // Verificar si ya está siguiendo
    const followingDoc = await getDoc(followingRef);

    if (followingDoc.exists()) {
      console.log(`El usuario ${currentUserId} ya sigue a ${targetUserId}`);
      return;
    }

    // Datos a guardar
    const followingData = {
      uid: targetUserId,
      displayName: targetUserData.displayName || 'Usuario',
      photoURL: targetUserData.photoURL || '/default-avatar.png',
      createdAt: serverTimestamp()
    };

    const followerData = {
      uid: currentUserId,
      displayName: currentUserData.displayName || 'Usuario',
      photoURL: currentUserData.photoURL || '/default-avatar.png',
      createdAt: serverTimestamp()
    };

    // Crear documentos en las subcollecciones
    await Promise.all([
      // Añadir a la lista de seguidos del usuario actual
      setDoc(followingRef, followingData),
      // Añadir a la lista de seguidores del usuario objetivo
      setDoc(followerRef, followerData),
      // Actualizar contadores
      updateUserFollowCount(currentUserId, 'following', 1),
      updateUserFollowCount(targetUserId, 'followers', 1)
    ]);

    // Sincronizar contadores para asegurar precisión
    await Promise.all([
      syncFollowCounts(currentUserId),
      syncFollowCounts(targetUserId)
    ]);

    // Crear notificación
    try {
      await createNotification({
        type: 'follow',
        fromUserId: currentUserId,
        fromUserName: currentUserData.displayName || 'Usuario',
        fromUserPhoto: currentUserData.photoURL || '',
        toUserId: targetUserId,
        read: false
      });
    } catch (notifError) {
      // Si falla la notificación, no interrumpimos el flujo principal
      console.error('Error al crear notificación de seguimiento:', notifError);
    }

    console.log(`Usuario ${currentUserId} ahora sigue a ${targetUserId}`);
  } catch (error) {
    console.error('Error al seguir usuario:', error);
    throw new Error('No se pudo seguir al usuario. Inténtalo de nuevo.');
  }
};

/**
 * Deja de seguir a un usuario
 * @param currentUserId ID del usuario actual
 * @param targetUserId ID del usuario a dejar de seguir
 * @returns Promise<void>
 */
export const unfollowUser = async (
  currentUserId: string,
  targetUserId: string
): Promise<void> => {
  // Validaciones iniciales
  if (!currentUserId || !targetUserId) {
    console.error('IDs de usuario inválidos');
    throw new Error('Datos de usuario inválidos');
  }

  if (currentUserId === targetUserId) {
    console.error('Un usuario no puede dejar de seguirse a sí mismo');
    return; // Simplemente retornamos sin error para evitar problemas en la UI
  }

  try {
    // Referencias a las subcollecciones
    const followingRef = doc(db, `users/${currentUserId}/following`, targetUserId);
    const followerRef = doc(db, `users/${targetUserId}/followers`, currentUserId);

    // Verificar si está siguiendo
    const followingDoc = await getDoc(followingRef);

    if (!followingDoc.exists()) {
      console.log(`El usuario ${currentUserId} no sigue a ${targetUserId}`);
      return;
    }

    // Eliminar documentos de las subcollecciones con manejo de errores individual
    const operations = [];

    // Eliminar de la lista de seguidos del usuario actual
    operations.push(
      deleteDoc(followingRef).catch(error => {
        console.error('Error al eliminar de following:', error);
      })
    );

    // Eliminar de la lista de seguidores del usuario objetivo
    operations.push(
      deleteDoc(followerRef).catch(error => {
        console.error('Error al eliminar de followers:', error);
      })
    );

    // Actualizar contadores
    operations.push(
      updateUserFollowCount(currentUserId, 'following', -1).catch(error => {
        console.error('Error al actualizar contador de following:', error);
      })
    );

    operations.push(
      updateUserFollowCount(targetUserId, 'followers', -1).catch(error => {
        console.error('Error al actualizar contador de followers:', error);
      })
    );

    // Eliminar notificación de seguimiento si existe
    try {
      const notificationQuery = query(
        collection(db, 'notifications'),
        where('fromUserId', '==', currentUserId),
        where('toUserId', '==', targetUserId),
        where('type', '==', 'follow')
      );

      const notificationSnapshot = await getDocs(notificationQuery);
      notificationSnapshot.forEach(async (docItem) => {
        await deleteDoc(docItem.ref);
      });
    } catch (notifError) {
      console.error('Error al eliminar notificación:', notifError);
    }

    // Ejecutar todas las operaciones, incluso si algunas fallan
    await Promise.allSettled(operations);

    // Sincronizar contadores para asegurar precisión
    await Promise.allSettled([
      syncFollowCounts(currentUserId),
      syncFollowCounts(targetUserId)
    ]);

    console.log(`Usuario ${currentUserId} dejó de seguir a ${targetUserId}`);
  } catch (error) {
    console.error('Error al dejar de seguir usuario:', error);
    throw new Error('No se pudo dejar de seguir al usuario. Inténtalo de nuevo.');
  }
};

/**
 * Verifica si un usuario sigue a otro
 * @param currentUserId ID del usuario actual
 * @param targetUserId ID del usuario a verificar
 * @returns Promise<boolean>
 */
export const checkIfFollowing = async (
  currentUserId: string,
  targetUserId: string
): Promise<boolean> => {
  try {
    if (!currentUserId || !targetUserId || currentUserId === targetUserId) {
      return false;
    }

    const followingRef = doc(db, `users/${currentUserId}/following`, targetUserId);
    const followingDoc = await getDoc(followingRef);

    return followingDoc.exists();
  } catch (error) {
    console.error('Error al verificar estado de seguimiento:', error);
    return false;
  }
};

/**
 * Actualiza el contador de seguidores o seguidos de un usuario
 * @param userId ID del usuario
 * @param field Campo a actualizar ('followers' o 'following')
 * @param value Valor a incrementar o decrementar
 * @returns Promise<void>
 */
export const updateUserFollowCount = async (
  userId: string,
  field: 'followers' | 'following',
  value: number
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      [field]: increment(value),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error(`Error al actualizar contador de ${field}:`, error);
    throw new Error(`No se pudo actualizar el contador de ${field}.`);
  }
};

/**
 * Sincroniza los contadores de seguidores y seguidos con las subcollections reales
 * @param userId ID del usuario
 * @returns Promise<void>
 */
export const syncFollowCounts = async (userId: string): Promise<void> => {
  if (!userId) return;

  try {
    // Referencias a las subcollections
    const followersRef = collection(db, `users/${userId}/followers`);
    const followingRef = collection(db, `users/${userId}/following`);

    // Obtener el número real de documentos en cada subcollection
    const [followersSnapshot, followingSnapshot] = await Promise.all([
      getDocs(followersRef),
      getDocs(followingRef)
    ]);

    const followerCount = followersSnapshot.size;
    const followingCount = followingSnapshot.size;

    // Referencia al documento del usuario
    const userRef = doc(db, 'users', userId);

    // Actualizar los contadores en el documento del usuario
    await updateDoc(userRef, {
      followers: followerCount,
      following: followingCount,
      updatedAt: serverTimestamp()
    });

    console.log(`Contadores sincronizados para usuario ${userId}: ${followerCount} seguidores, ${followingCount} seguidos`);
  } catch (error) {
    console.error('Error al sincronizar contadores de seguimiento:', error);
  }
};

/**
 * Sincroniza los contadores de seguidores y seguidos para todos los usuarios
 * @returns Promise<{success: number, failed: number}> Número de usuarios actualizados con éxito y fallidos
 */
export const syncAllUsersFollowCounts = async (): Promise<{ success: number, failed: number }> => {
  try {
    // Obtener todos los usuarios
    const usersSnapshot = await getDocs(collection(db, 'users'));

    let success = 0;
    let failed = 0;

    // Procesar cada usuario
    const promises = usersSnapshot.docs.map(async (userDoc) => {
      try {
        await syncFollowCounts(userDoc.id);
        success++;
      } catch (error) {
        console.error(`Error al sincronizar contadores para usuario ${userDoc.id}:`, error);
        failed++;
      }
    });

    // Esperar a que todas las operaciones terminen
    await Promise.allSettled(promises);

    console.log(`Sincronización de contadores completada: ${success} usuarios actualizados, ${failed} fallidos`);
    return { success, failed };
  } catch (error) {
    console.error('Error al sincronizar contadores de todos los usuarios:', error);
    return { success: 0, failed: 0 };
  }
};

/**
 * Obtiene la lista de seguidores de un usuario
 * @param userId ID del usuario
 * @param currentUserId ID del usuario actual (opcional, para verificar si los sigue)
 * @returns Promise<FollowUser[]>
 */
export const getFollowers = async (
  userId: string,
  currentUserId?: string
): Promise<FollowUser[]> => {
  try {
    const followersRef = collection(db, `users/${userId}/followers`);
    const followersQuery = query(followersRef, orderBy('createdAt', 'desc'));
    const followersSnapshot = await getDocs(followersQuery);

    const followers: FollowUser[] = [];

    // Si hay un usuario actual, verificar a quién sigue
    let currentUserFollowing: string[] = [];

    if (currentUserId) {
      const followingRef = collection(db, `users/${currentUserId}/following`);
      const followingSnapshot = await getDocs(followingRef);
      currentUserFollowing = followingSnapshot.docs.map(doc => doc.id);
    }

    followersSnapshot.forEach(doc => {
      const followerData = doc.data() as FollowUser;
      followers.push({
        ...followerData,
        uid: doc.id,
        isFollowing: currentUserId ? currentUserFollowing.includes(doc.id) || doc.id === currentUserId : false
      });
    });

    return followers;
  } catch (error) {
    console.error('Error al obtener seguidores:', error);
    throw new Error('No se pudieron cargar los seguidores. Inténtalo de nuevo.');
  }
};

/**
 * Obtiene la lista de usuarios que sigue un usuario
 * @param userId ID del usuario
 * @param currentUserId ID del usuario actual (opcional, para verificar si los sigue)
 * @returns Promise<FollowUser[]>
 */
export const getFollowing = async (
  userId: string,
  currentUserId?: string
): Promise<FollowUser[]> => {
  try {
    const followingRef = collection(db, `users/${userId}/following`);
    const followingQuery = query(followingRef, orderBy('createdAt', 'desc'));
    const followingSnapshot = await getDocs(followingQuery);

    const following: FollowUser[] = [];

    // Si hay un usuario actual, verificar a quién sigue
    let currentUserFollowing: string[] = [];

    if (currentUserId && currentUserId !== userId) {
      const userFollowingRef = collection(db, `users/${currentUserId}/following`);
      const userFollowingSnapshot = await getDocs(userFollowingRef);
      currentUserFollowing = userFollowingSnapshot.docs.map(doc => doc.id);
    } else if (currentUserId === userId) {
      // Si el usuario actual es el mismo que estamos consultando, ya sabemos que sigue a todos en la lista
      currentUserFollowing = followingSnapshot.docs.map(doc => doc.id);
    }

    followingSnapshot.forEach(doc => {
      const followingData = doc.data() as FollowUser;
      following.push({
        ...followingData,
        uid: doc.id,
        isFollowing: currentUserId ? currentUserFollowing.includes(doc.id) || doc.id === currentUserId : false
      });
    });

    return following;
  } catch (error) {
    console.error('Error al obtener seguidos:', error);
    throw new Error('No se pudieron cargar los usuarios seguidos. Inténtalo de nuevo.');
  }
};

/**
 * Configura un listener para la lista de seguidores de un usuario
 * @param userId ID del usuario
 * @param currentUserId ID del usuario actual (opcional, para verificar si los sigue)
 * @param callback Función a ejecutar cuando cambie la lista
 * @returns Función para cancelar el listener
 */
export const listenToFollowers = (
  userId: string,
  currentUserId: string | undefined,
  callback: (followers: FollowUser[]) => void
): Unsubscribe => {
  const followersRef = collection(db, `users/${userId}/followers`);
  const followersQuery = query(followersRef, orderBy('createdAt', 'desc'));

  return onSnapshot(followersQuery, async (snapshot) => {
    try {
      const followers: FollowUser[] = [];

      // Si hay un usuario actual, verificar a quién sigue
      let currentUserFollowing: string[] = [];

      if (currentUserId) {
        const followingRef = collection(db, `users/${currentUserId}/following`);
        const followingSnapshot = await getDocs(followingRef);
        currentUserFollowing = followingSnapshot.docs.map(doc => doc.id);
      }

      snapshot.forEach(doc => {
        const followerData = doc.data() as FollowUser;
        followers.push({
          ...followerData,
          uid: doc.id,
          isFollowing: currentUserId ? currentUserFollowing.includes(doc.id) || doc.id === currentUserId : false
        });
      });

      callback(followers);
    } catch (error) {
      console.error('Error en el listener de seguidores:', error);
    }
  }, (error) => {
    console.error('Error en el listener de seguidores:', error);
  });
};

/**
 * Configura un listener para la lista de usuarios que sigue un usuario
 * @param userId ID del usuario
 * @param currentUserId ID del usuario actual (opcional, para verificar si los sigue)
 * @param callback Función a ejecutar cuando cambie la lista
 * @returns Función para cancelar el listener
 */
export const listenToFollowing = (
  userId: string,
  currentUserId: string | undefined,
  callback: (following: FollowUser[]) => void
): Unsubscribe => {
  const followingRef = collection(db, `users/${userId}/following`);
  const followingQuery = query(followingRef, orderBy('createdAt', 'desc'));

  return onSnapshot(followingQuery, async (snapshot) => {
    try {
      const following: FollowUser[] = [];

      // Si hay un usuario actual, verificar a quién sigue
      let currentUserFollowing: string[] = [];

      if (currentUserId && currentUserId !== userId) {
        const userFollowingRef = collection(db, `users/${currentUserId}/following`);
        const userFollowingSnapshot = await getDocs(userFollowingRef);
        currentUserFollowing = userFollowingSnapshot.docs.map(doc => doc.id);
      } else if (currentUserId === userId) {
        // Si el usuario actual es el mismo que estamos consultando, ya sabemos que sigue a todos en la lista
        currentUserFollowing = snapshot.docs.map(doc => doc.id);
      }

      snapshot.forEach(doc => {
        const followingData = doc.data() as FollowUser;
        following.push({
          ...followingData,
          uid: doc.id,
          isFollowing: currentUserId ? currentUserFollowing.includes(doc.id) || doc.id === currentUserId : false
        });
      });

      callback(following);
    } catch (error) {
      console.error('Error en el listener de seguidos:', error);
    }
  }, (error) => {
    console.error('Error en el listener de seguidos:', error);
  });
};

/**
 * Configura un listener para el estado de seguimiento entre dos usuarios
 * @param currentUserId ID del usuario actual
 * @param targetUserId ID del usuario objetivo
 * @param callback Función a ejecutar cuando cambie el estado
 * @returns Función para cancelar el listener
 */
export const listenToFollowStatus = (
  currentUserId: string,
  targetUserId: string,
  callback: (isFollowing: boolean) => void
): Unsubscribe => {
  if (!currentUserId || !targetUserId || currentUserId === targetUserId) {
    callback(false);
    // Devolver una función vacía como unsubscribe
    return () => { };
  }

  const followingRef = doc(db, `users/${currentUserId}/following`, targetUserId);

  return onSnapshot(followingRef, (doc) => {
    callback(doc.exists());
  }, (error) => {
    console.error('Error en el listener de estado de seguimiento:', error);
    callback(false);
  });
};

/**
 * Obtiene sugerencias de usuarios para seguir
 * @param currentUserId ID del usuario actual
 * @param limit Límite de sugerencias a obtener
 * @returns Promise<FollowUser[]>
 */
export const getSuggestedUsers = async (
  currentUserId: string,
  limitCount: number = 5
): Promise<FollowUser[]> => {
  try {
    if (!currentUserId) return [];

    // Obtener usuarios que el usuario actual ya sigue
    const followingRef = collection(db, `users/${currentUserId}/following`);
    const followingSnapshot = await getDocs(followingRef);
    const followingIds = followingSnapshot.docs.map(doc => doc.id);

    // Añadir el ID del usuario actual para excluirlo de las sugerencias
    followingIds.push(currentUserId);

    // Consultar usuarios que el usuario actual no sigue
    const usersRef = collection(db, 'users');
    const usersQuery = query(
      usersRef,
      // Ordenar por fecha de creacion en lugar de lastActive
      orderBy('createdAt', 'desc'),
      limit(limitCount + followingIds.length * 2) // Obtener más usuarios para compensar los que ya sigue
    );

    const usersSnapshot = await getDocs(usersQuery);
    const suggestedUsers: FollowUser[] = [];

    // Procesar los resultados de manera segura
    for (const doc of usersSnapshot.docs) {
      // Excluir usuarios que ya sigue y al propio usuario
      if (!followingIds.includes(doc.id)) {
        try {
          const userData = doc.data();
          // Asegurarse de que todos los campos necesarios existen
          if (doc.id && userData) {
            suggestedUsers.push({
              uid: doc.id,
              displayName: userData.displayName || 'Usuario',
              photoURL: userData.photoURL || '/default-avatar.png',
              bio: userData.bio || '',
              isFollowing: false
            });
          }
        } catch (docError) {
          console.error('Error procesando documento de usuario:', docError);
          // Continuar con el siguiente documento
          continue;
        }
      }
    }

    // Si no hay suficientes usuarios ordenados por createdAt, intentar sin ordenar
    if (suggestedUsers.length < limitCount) {
      try {
        const fallbackQuery = query(
          usersRef,
          limit(limitCount * 3) // Intentar con más usuarios
        );

        const fallbackSnapshot = await getDocs(fallbackQuery);

        // Procesar los resultados de manera segura
        for (const doc of fallbackSnapshot.docs) {
          // Evitar duplicados y excluir usuarios que ya sigue
          if (!followingIds.includes(doc.id) && !suggestedUsers.some(u => u.uid === doc.id)) {
            try {
              const userData = doc.data();
              // Asegurarse de que todos los campos necesarios existen
              if (doc.id && userData) {
                suggestedUsers.push({
                  uid: doc.id,
                  displayName: userData.displayName || 'Usuario',
                  photoURL: userData.photoURL || '/default-avatar.png',
                  bio: userData.bio || '',
                  isFollowing: false
                });
              }
            } catch (docError) {
              console.error('Error procesando documento de usuario (fallback):', docError);
              // Continuar con el siguiente documento
              continue;
            }
          }
        }
      } catch (fallbackError) {
        console.error('Error en la consulta fallback:', fallbackError);
        // Continuar con los usuarios que ya tenemos
      }
    }

    // Asegurarse de que todos los objetos son válidos antes de devolverlos
    const validUsers = suggestedUsers.filter(user =>
      user && typeof user === 'object' && user.uid && typeof user.uid === 'string'
    );

    // Limitar al número solicitado
    return validUsers.slice(0, limitCount);
  } catch (error) {
    console.error('Error al obtener sugerencias de usuarios:', error);
    return [];
  }
};
